import {
  MAX_DURATION_SEC,
  MIN_DURATION_SEC,
  getClipLimitForModel,
  snapClipDuration,
} from "./durationOptions";
import { expandMentionsInPrompt, collectMentionableAssets } from "./mentionAssets";

/** Whether the UI model is a Kling family model (excludes motion control — single-shot API). */
export function isKlingUiModel(modelId) {
  const id = String(modelId || "").toLowerCase();
  if (id.includes("motion-control")) return false;
  return id.includes("kling");
}

/**
 * True when target duration exceeds one API clip and the model supports auto-stitch.
 * @param {string} modelId
 * @param {number} durationSec
 * @param {{ hailuoResolution?: string; resolution?: string }} [opts]
 */
export function needsAutoStitch(modelId, durationSec, opts = {}) {
  const limit = getClipLimitForModel(modelId, opts);
  if (!limit.stitchable || limit.maxSec == null) return false;
  return Number(durationSec) > limit.maxSec;
}

/** @deprecated Use needsAutoStitch — kept for older imports. */
export function needsKlingStitch(modelId, durationSec, opts = {}) {
  return needsAutoStitch(modelId, durationSec, opts);
}

/**
 * Split a target duration into API-safe clip lengths for the selected model.
 * Discrete-only models (e.g. Veo 8s) may slightly overshoot the target.
 * @param {number} totalSec
 * @param {string} [modelId]
 * @param {{ hailuoResolution?: string; resolution?: string }} [opts]
 * @returns {number[]}
 */
export function distributeSegmentDurations(totalSec, modelId, opts = {}) {
  const limit = getClipLimitForModel(modelId, opts);
  const target = Math.min(
    MAX_DURATION_SEC,
    Math.max(MIN_DURATION_SEC, Math.round(Number(totalSec) || MIN_DURATION_SEC)),
  );

  if (!limit.stitchable || limit.maxSec == null) {
    return [snapClipDuration(target, limit)];
  }

  if (target <= limit.maxSec) {
    return [snapClipDuration(target, limit)];
  }

  // Fixed single allowed length (Veo 8s, Hailuo 1080p 6s).
  if (limit.allowed?.length === 1) {
    const clip = limit.allowed[0];
    const count = Math.max(1, Math.ceil(target / clip));
    return Array(count).fill(clip);
  }

  // Discrete allowed set (Runway 5/10, Sora 15/25, Hailuo 6/10).
  if (limit.allowed?.length) {
    const clipMax = limit.maxSec;
    const count = Math.max(1, Math.ceil(target / clipMax));
    const base = Math.floor(target / count);
    const rem = target - base * count;
    return Array.from({ length: count }, (_, i) =>
      snapClipDuration(i < rem ? base + 1 : base, limit),
    );
  }

  // Continuous range (Kling 3–15, Wan 2–10).
  const clipMax = limit.maxSec;
  const clipMin = limit.minSec;
  const fullClips = Math.floor((target - clipMin) / clipMax);
  const remainder = target - fullClips * clipMax;
  const segments = Array(fullClips).fill(clipMax);

  if (remainder >= clipMin) {
    segments.push(remainder);
  } else if (segments.length > 0) {
    const borrow = clipMin - remainder;
    segments[segments.length - 1] -= borrow;
    segments.push(clipMin);
  } else {
    segments.push(clipMin);
  }

  return segments.filter((d) => d >= clipMin && d <= clipMax);
}

/**
 * Split a target duration (3–300s) into Kling-safe segments (each 3–15s).
 * @param {number} totalSec
 * @param {string} [modelId]
 * @param {{ hailuoResolution?: string; resolution?: string }} [opts]
 * @returns {number[]}
 */
export function distributeKlingSegmentDurations(totalSec, modelId = "kling-3.0", opts = {}) {
  return distributeSegmentDurations(totalSec, modelId, opts);
}

/** Split prose into sentence-like chunks for per-segment script assignment. */
function splitIntoSentences(text) {
  const trimmed = String(text || "").trim();
  if (!trimmed) return [];
  const parts = trimmed.match(/[^.!?…]+[.!?…]+|[^.!?…]+$/g);
  if (parts?.length) return parts.map((s) => s.trim()).filter(Boolean);
  return [trimmed];
}

/** Section headers that are never spoken dialogue labels. */
const NON_SPEAKER_LABELS =
  /^(Audio|Negative prompts?|Camera|Subject|Voice|Lip sync|Reference|Notes?|Environment|Style|Duration|Aspect|Commercial|Concept|Composition|Framing|Prompt|Title|Model|Scene)$/i;

function finalizeDialogueExtract(full, labelIndex, labelLength, labelKeepInBrief) {
  const start = labelIndex + labelLength;
  let rest = full.slice(start);
  const nextSection = rest.search(
    /(?:\n|\s{2,}|\.\s+)\s*(?:Audio|Negative prompts?|Camera|Subject|Voice|Lip sync|Reference|Notes?|Environment|Style)\s*:/i,
  );
  if (nextSection >= 0) rest = rest.slice(0, nextSection);
  let dialogue = rest.trim();
  const quoted = dialogue.match(/^["“]([\s\S]*?)["”]\s*$/);
  if (quoted) dialogue = quoted[1].trim();
  else {
    const inner = dialogue.match(/^["“]([\s\S]*?)["”]/);
    if (inner) dialogue = inner[1].trim();
  }
  if (!dialogue) return null;
  if (/^\(?\s*none for this clip\b/i.test(dialogue)) return null;
  const before = full.slice(0, labelIndex).trim();
  const after = full.slice(start + rest.length).trim();
  const keep = String(labelKeepInBrief || "").trim();
  const brief = [before, keep, after].filter(Boolean).join("\n").trim();
  return { brief, dialogue, hasDialogue: true };
}

/** Strip [@Name] / @Name / @[Name](img:…) down to a plain speaker label. */
export function normalizeSpeakerLabel(raw) {
  let s = String(raw || "").trim();
  const token = s.match(/^@\[([^\]]+)\]\([^)]+\)$/);
  if (token) return token[1].trim();
  s = s.replace(/^\[@?/, "").replace(/\]$/, "").replace(/^@/, "").trim();
  return s;
}

function parseSpeakerCue(raw) {
  const token = String(raw || "").trim();
  const m = token.match(
    /^@\[([^\]]+)\]\((?:img|vid):(imageUrlUploads|referenceImageUploads|referenceVideoUploads|sceneImage|sceneVideo|studio):(\d+)\)$/i,
  );
  if (m) {
    return { label: m[1].trim(), bucket: m[2], index: Number(m[3]) };
  }
  return { label: normalizeSpeakerLabel(token), bucket: null, index: null };
}

/**
 * Split multi-speaker prompts into ordered spoken lines.
 * @returns {{ speaker: string, text: string, bucket: string|null, index: number|null }[]}
 */
export function extractDialogueLinesFromPrompt(fullPrompt) {
  const full = String(fullPrompt || "").trim();
  if (!full) return [];

  const cueRe =
    /(?:^|[\n\r])\s*((?:@\[[^\]]+\]\([^)]+\)|\[@[^\]]+\]|\[[^\]]+\]|@[^\n:\[\]]+|[A-Z][a-zA-Z0-9_'-]{0,40}))\s*:\s*/g;
  /** @type {RegExpExecArray[]} */
  const cues = [];
  let m;
  while ((m = cueRe.exec(full))) {
    const parsed = parseSpeakerCue(m[1]);
    const plain = parsed.label;
    if (
      /^[A-Z][a-zA-Z0-9_'-]{0,40}$/.test(String(m[1] || "").trim()) &&
      NON_SPEAKER_LABELS.test(plain)
    ) {
      continue;
    }
    if (
      /^(Dialogue|Spoken dialogue|Script|Lines?|Say(?:s|ing)?|Voiceovers?|VO|Narrat(?:ion|or)|Spoken)$/i.test(
        plain,
      )
    ) {
      continue;
    }
    cues.push(m);
  }

  if (!cues.length) return [];

  /** @type {{ speaker: string, text: string, bucket: string|null, index: number|null }[]} */
  const lines = [];
  for (let i = 0; i < cues.length; i++) {
    const cue = cues[i];
    const start = cue.index + cue[0].length;
    const end = i + 1 < cues.length ? cues[i + 1].index : full.length;
    let text = full.slice(start, end);
    const nextSection = text.search(
      /(?:\n|\s{2,}|\.\s+)\s*(?:Audio|Negative prompts?|Camera|Subject|Voice|Lip sync|Reference|Notes?|Environment|Style)\s*:/i,
    );
    if (nextSection >= 0) text = text.slice(0, nextSection);
    text = text.trim();
    const quoted = text.match(/^["“]([\s\S]*?)["”]\s*$/);
    if (quoted) text = quoted[1].trim();
    if (!text || /^\(?\s*none for this clip\b/i.test(text)) continue;
    const parsed = parseSpeakerCue(cue[1]);
    lines.push({
      speaker: parsed.label,
      text,
      bucket: parsed.bucket,
      index: parsed.index,
    });
  }
  return lines;
}

/**
 * Pull spoken dialogue out of a structured prompt / scene line.
 * @param {string} fullPrompt
 * @returns {{ brief: string; dialogue: string; hasDialogue: boolean }}
 */
export function extractDialogueFromPrompt(fullPrompt) {
  const full = String(fullPrompt || "").trim();
  if (!full) return { brief: "", dialogue: "", hasDialogue: false };

  const speakerLines = extractDialogueLinesFromPrompt(full);
  if (speakerLines.length > 0) {
    const dialogue = speakerLines
      .map((l) => l.text)
      .join("\n")
      .trim();
    if (dialogue) {
      const firstCue = full.search(
        /(?:^|[\n\r])\s*(?:@\[[^\]]+\]\([^)]+\)|\[@[^\]]+\]|\[[^\]]+\]|@[^\n:\[\]]+|[A-Z][a-zA-Z0-9_'-]{0,40})\s*:/m,
      );
      const brief = firstCue > 0 ? full.slice(0, firstCue).trim() : "";
      return { brief, dialogue, hasDialogue: true };
    }
  }

  const labeled =
    full.match(
      /(?:^|[\n\r])\s*(?:Dialogue|Spoken dialogue|Script|Lines?|Say(?:s|ing)?|Voiceovers?|VO|Narrat(?:ion|or)|Spoken)(?:\s*\([^)]*\))?\s*:\s*/i,
    ) ||
    full.match(
      /\b(?:Dialogue|Spoken dialogue|Script|Voiceovers?|VO|Narrat(?:ion|or))(?:\s*\([^)]*\))?\s*:\s*/i,
    );
  if (labeled && labeled.index != null) {
    const got = finalizeDialogueExtract(full, labeled.index, labeled[0].length, "");
    if (got) return got;
  }

  const blocks = [...full.matchAll(/["“]([^"”]{12,})["”]/g)];
  if (blocks.length) {
    let best = blocks[0];
    for (const b of blocks) {
      if ((b[1] || "").length > (best[1] || "").length) best = b;
    }
    const dialogue = String(best[1] || "").trim();
    const brief = `${full.slice(0, best.index).trim()}\n${full.slice((best.index || 0) + best[0].length).trim()}`.trim();
    if (dialogue) return { brief, dialogue, hasDialogue: true };
  }

  const visualDirection =
    /(camera|lighting|cinematic|volumetric|negative prompt|aspect ratio|resolution|wardrobe|color grade|commercial|concept|composition|framing|low-?angle|hero framing|neon|haze|mood(?:y)?|atmosphere|bokeh|shallow depth|rain-?soaked|rooftop)/i;
  const wordCount = full.split(/\s+/).filter(Boolean).length;
  const looksSpoken =
    (/[.!?]"?\s*$/.test(full) && wordCount <= 40) ||
    (/^(?:I|I'm|I've|I'd|We|We're|You|He|She|They|That's|It's|That is)\b/i.test(full) &&
      wordCount <= 35);
  if (
    full.length <= 220 &&
    !/@|\[@/.test(full) &&
    !visualDirection.test(full) &&
    !/\b[A-Z][a-z]+(?:\s+[a-z]+){1,}\s*:/.test(full) &&
    looksSpoken
  ) {
    return { brief: "", dialogue: full, hasDialogue: true };
  }

  return { brief: full, dialogue: "", hasDialogue: false };
}

/**
 * Split dialogue (or whole prompt) into N chunks weighted by segment duration.
 * @param {string} text
 * @param {number} segmentCount
 * @param {number[]} segmentDurations
 * @returns {string[]}
 */
function splitTextByDuration(text, segmentCount, segmentDurations) {
  const full = String(text || "").trim();
  if (!full || segmentCount <= 1) return [full];

  const durations = (segmentDurations || []).slice(0, segmentCount);
  while (durations.length < segmentCount) durations.push(15);
  const totalDur = durations.reduce((sum, d) => sum + d, 0) || segmentCount;

  // Prefer paragraph breaks inside dialogue, then sentences, then words.
  const paragraphs = full.split(/\n+/).map((p) => p.trim()).filter(Boolean);
  const sentences = splitIntoSentences(full);
  const units =
    paragraphs.length >= segmentCount
      ? paragraphs
      : sentences.length >= segmentCount
        ? sentences
        : full.split(/\s+/).filter(Boolean);

  const chunks = Array.from({ length: segmentCount }, () => []);
  let unitIdx = 0;

  for (let seg = 0; seg < segmentCount; seg += 1) {
    const isLast = seg === segmentCount - 1;
    const weight = durations[seg] / totalDur;
    const take = isLast
      ? units.length - unitIdx
      : Math.max(1, Math.round(units.length * weight));

    for (let i = 0; i < take && unitIdx < units.length; i += 1) {
      chunks[seg].push(units[unitIdx]);
      unitIdx += 1;
    }
  }

  while (unitIdx < units.length) {
    chunks[segmentCount - 1].push(units[unitIdx]);
    unitIdx += 1;
  }

  const joiner = paragraphs.length >= segmentCount ? "\n\n" : " ";
  return chunks.map((parts, i) => {
    const joined = parts.join(joiner).trim();
    if (joined) return joined;
    if (i === 0) return full;
    return "";
  });
}

/**
 * Divide a script across N segments proportional to each segment's duration.
 * When a Dialogue block exists, only that spoken text is split; the production
 * brief is kept intact on every clip via segmentContinuityPrompt.
 * @param {string} fullPrompt
 * @param {number} segmentCount
 * @param {number[]} segmentDurations
 * @returns {string[]}
 */
export function splitPromptAcrossSegments(fullPrompt, segmentCount, segmentDurations) {
  const full = String(fullPrompt || "").trim();
  if (!full || segmentCount <= 1) return [full];

  // Multi-scene: only an explicit Dialogue:/Voiceover:/Name:/@tag: block is spoken.
  // Never carve a commercial/shared visual brief into per-scene "scripts".
  const { dialogue, hasDialogue } = extractDialogueFromPrompt(full);
  if (hasDialogue) {
    return splitTextByDuration(dialogue, segmentCount, segmentDurations);
  }
  return Array.from({ length: segmentCount }, () => "");
}

/** Embedded in every multi-segment script sent to the model. */
export const NO_REPETITION_DIRECTIVE =
  "CRITICAL — No repetition: never repeat any dialogue, action, gesture, or story beat from a previous clip or from earlier in this clip. " +
  "Every word must be new and forward-moving only — do not rewind, recap, or re-deliver content already shown.";

/** Forbid inventing lines when a written dialogue block exists. */
export const EXACT_DIALOGUE_DIRECTIVE =
  "CRITICAL — Exact dialogue only: speak the Dialogue lines below word-for-word. " +
  "Do NOT invent, paraphrase, summarize, or add any other spoken lines. " +
  "Do NOT talk about the room, workspace, product props, or camera unless those words appear in the Dialogue.";

/** Voice consistency rules for stitched segments. */
export const VOICE_CONSISTENCY_DIRECTIVE =
  "CRITICAL — Voice consistency: use the exact same speaker voice, accent, pitch, tone, cadence, and speaking pace as the previous clip. " +
  "Do not change voice, age, gender, or vocal character. Lip-sync and delivery must match the established speaker.";

/** Visual identity lock for stitched segments — ties every clip to the uploaded character. */
export const CHARACTER_CONSISTENCY_DIRECTIVE =
  "CRITICAL — Character identity: the on-screen subject MUST be the exact same person shown in the reference character image(s). " +
  "Same gender, face, facial features, hair, skin tone, age, and body type. " +
  "Do NOT change character, do NOT substitute a different person, and do NOT change gender. " +
  "Wardrobe, lighting, and color grade should stay consistent unless the script explicitly requires a change.";

/**
 * Original user-uploaded character images — reused on every stitched segment as identity anchors.
 * @param {object} baseForm
 * @returns {object[]}
 */
export function getAnchorCharacterUploads(baseForm) {
  const seen = new Set();
  /** @type {object[]} */
  const out = [];
  for (const group of [baseForm?.imageUrlUploads, baseForm?.referenceImageUploads]) {
    for (const u of group || []) {
      const url = u?.previewUrl;
      if (!url || seen.has(url)) continue;
      seen.add(url);
      out.push({
        ...u,
        id: u.id || `anchor-char-${out.length}`,
      });
    }
  }
  return out.slice(0, 4);
}

/**
 * Prompt for one stitched segment — full brief + this clip's dialogue only.
 * @param {string} basePrompt
 * @param {number} segmentIndex
 * @param {number} totalSegments
 * @param {string} segmentScript
 * @param {{ multiSpeaker?: boolean }} [opts]
 */
export function segmentContinuityPrompt(
  basePrompt,
  segmentIndex,
  totalSegments,
  segmentScript,
  opts = {},
) {
  const multiSpeaker = opts?.multiSpeaker === true;
  const sceneParsed = extractDialogueFromPrompt(String(segmentScript ?? "").trim());

  // Single clip: main/shared prompt may carry Dialogue: (or the whole line may be spoken).
  if (totalSegments <= 1) {
    const { brief, dialogue, hasDialogue } = extractDialogueFromPrompt(basePrompt);
    if (sceneParsed.hasDialogue) {
      const visual = [hasDialogue ? brief : String(basePrompt || "").trim(), sceneParsed.brief]
        .filter(Boolean)
        .join("\n")
        .trim();
      return [visual, `Dialogue:\n${sceneParsed.dialogue}`].filter(Boolean).join("\n\n").trim();
    }
    if (hasDialogue) {
      return [brief, `Dialogue:\n${dialogue}`].filter(Boolean).join("\n\n").trim();
    }
    return String(basePrompt || segmentScript || "").trim();
  }

  // Multi-scene: shared brief is visuals/constraints only — never spoken.
  const { brief: sharedVisual } = extractDialogueFromPrompt(basePrompt);
  const productionBrief = [
    sharedVisual || String(basePrompt || "").trim(),
    sceneParsed.hasDialogue ? sceneParsed.brief : "",
  ]
    .filter(Boolean)
    .join("\n")
    .trim();

  const dialogueChunk = sceneParsed.hasDialogue ? sceneParsed.dialogue : "";
  const hasDialogue = sceneParsed.hasDialogue;
  const dialogueBlock = dialogueChunk
    ? `Dialogue:\n"${dialogueChunk}"`
    : "Dialogue: (none for this clip — hold natural eye contact and a brief pause; do not invent speech.)";

  if (multiSpeaker) {
    const turnRules = hasDialogue
      ? `${EXACT_DIALOGUE_DIRECTIVE} ${NO_REPETITION_DIRECTIVE}`
      : `${NO_REPETITION_DIRECTIVE}`;
    return (
      `Dialogue turn (${segmentIndex + 1} of ${totalSegments}) for ONE character only. ` +
      `The uploaded start-frame image is the sole on-camera subject for this clip — do not introduce other people. ` +
      `Match that person's face, wardrobe, and setting. Perform ONLY this clip's dialogue. ` +
      `${turnRules}\n\n` +
      `${productionBrief ? `Production brief (visuals/constraints — not extra spoken lines):\n${productionBrief}\n\n` : ""}` +
      dialogueBlock
    ).trim();
  }

  const sharedRules = hasDialogue
    ? `${CHARACTER_CONSISTENCY_DIRECTIVE} ${EXACT_DIALOGUE_DIRECTIVE} ${NO_REPETITION_DIRECTIVE} ${VOICE_CONSISTENCY_DIRECTIVE}`
    : `${CHARACTER_CONSISTENCY_DIRECTIVE} ${NO_REPETITION_DIRECTIVE} ${VOICE_CONSISTENCY_DIRECTIVE}`;

  if (segmentIndex === 0) {
    return (
      `Opening segment (1 of ${totalSegments}). Establish the subject, character, wardrobe, lighting, and setting clearly. ` +
      `Use the uploaded character reference image as the definitive identity for this person in every clip. ` +
      `Perform ONLY this clip's dialogue — do not skip ahead to later lines. ` +
      `${sharedRules}\n\n` +
      `${productionBrief ? `Production brief (visuals/voice/constraints — not extra spoken lines):\n${productionBrief}\n\n` : ""}` +
      dialogueBlock
    ).trim();
  }

  return (
    `Continuation segment (${segmentIndex + 1} of ${totalSegments}). ` +
    `The previous clip already delivered earlier dialogue — do NOT repeat those lines. ` +
    `Same person, face, gender, voice, wardrobe, lighting, color grade, and environment. ` +
    `${sharedRules}\n\n` +
    `${productionBrief ? `Production brief (visuals/voice/constraints — not extra spoken lines):\n${productionBrief}\n\n` : ""}` +
    dialogueBlock
  ).trim();
}

/**
 * Lock voice preset or chain reference audio for stitched segment generation.
 * @param {object} form
 * @param {{
 *   segmentIndex: number;
 *   voiceLock?: { mode: string; id?: string; label?: string } | null;
 *   chainAudioUrl?: string;
 * }} opts
 */
export function applyStitchVoiceSettings(form, opts) {
  const { segmentIndex, voiceLock, chainAudioUrl } = opts;
  if (form.sound === false || !voiceLock) return form;

  const next = { ...form, sound: true };
  const hasSceneAudio = (next.referenceAudioUploads || []).some(
    (u) => u?.previewUrl || u?.file,
  );
  // Scene-level audio references are explicit user intent; do not override them
  // with auto-extracted chain audio.
  if (hasSceneAudio) {
    next.voiceSource = "upload";
    return next;
  }

  if (segmentIndex > 0 && chainAudioUrl) {
    next.voiceSource = "upload";
    next.referenceAudioUploads = [
      {
        id: `chain-audio-${segmentIndex}`,
        previewUrl: chainAudioUrl,
        name: `segment-${segmentIndex + 1}-voice.mp3`,
      },
    ];
    if (voiceLock.mode === "preset" && voiceLock.id) {
      next.klingVoicePreset = voiceLock.id;
      next.klingVoiceLabel = voiceLock.label || "";
    }
    return next;
  }

  if (voiceLock.mode === "preset") {
    next.voiceSource = "preset";
    next.klingVoicePreset = voiceLock.id;
    next.klingVoiceLabel = voiceLock.label || "";
    if (segmentIndex === 0) {
      next.referenceAudioUploads = [];
    }
    return next;
  }

  return next;
}

/**
 * Build form state for one Kling segment in a stitched run.
 * @param {object} baseForm
 * @param {{
 *   segmentIndex: number;
 *   totalSegments: number;
 *   segmentDuration: number;
 *   segmentDurations?: number[];
 *   chainFrameUrl?: string;
 *   voiceLock?: object;
 *   chainAudioUrl?: string;
 * }} opts
 */
export function buildKlingSegmentForm(baseForm, opts) {
  const {
    segmentIndex,
    totalSegments,
    segmentDuration,
    segmentDurations,
    chainFrameUrl,
    voiceLock,
    chainAudioUrl,
  } = opts;
  const durations = segmentDurations?.length
    ? segmentDurations
    : Array.isArray(baseForm.scenes) && baseForm.scenes.length
      ? baseForm.scenes.map((s) => Number(s.duration) || 0).filter((d) => d > 0)
      : distributeSegmentDurations(baseForm.duration, baseForm.model, {
          hailuoResolution: baseForm.hailuoResolution,
          resolution: baseForm.resolution,
        });
  const scenePromptRaw = String(baseForm.scenes?.[segmentIndex]?.prompt || "").trim();
  const sceneAssets = collectMentionableAssets(baseForm, { sceneIndex: segmentIndex });
  const rootAssets = collectMentionableAssets(baseForm);
  // Parse spoken line BEFORE expanding @mentions so "@Image: Hello" stays dialogue, not a spoken tag.
  const sceneParsed = extractDialogueFromPrompt(scenePromptRaw);
  const sceneVisualRaw = sceneParsed.hasDialogue
    ? sceneParsed.brief || ""
    : scenePromptRaw;
  const sceneVisual = expandMentionsInPrompt(sceneVisualRaw, sceneAssets);
  const sharedBrief = expandMentionsInPrompt(String(baseForm.prompt || "").trim(), rootAssets);
  const sceneImages = (baseForm.scenes?.[segmentIndex]?.imageUrlUploads || []).filter(
    (u) => u?.previewUrl || u?.file,
  );
  const sceneAudios = sceneImages
    .map((img) => img?.voiceClip)
    .filter((v) => v?.previewUrl || v?.file);
  const rootAudios = [
    ...(baseForm.imageUrlUploads || []),
    ...(baseForm.referenceImageUploads || []),
  ]
    .map((img) => img?.voiceClip)
    .filter((v) => v?.previewUrl || v?.file);
  // Prefer this scene's mic, then fall back to root character voice.
  const characterAudios = sceneAudios.length ? sceneAudios : rootAudios;
  const promptChunks = scenePromptRaw
    ? null
    : splitPromptAcrossSegments(sharedBrief, totalSegments, durations);
  // Multi-scene: only scene dialogue (or Dialogue: split from brief into empty scenes).
  // Never speak the commercial/shared visual brief.
  const segmentScript = sceneParsed.hasDialogue
    ? sceneParsed.dialogue
    : scenePromptRaw
      ? sceneVisual
      : String(promptChunks?.[segmentIndex] || "").trim();

  const form = {
    ...baseForm,
    _segmentDuration: segmentDuration,
    duration: segmentDuration,
    multiShots: false,
    // Per-scene library voice for multi-speaker dialogue (overrides root characterVoiceId).
    characterVoiceId:
      String(baseForm.scenes?.[segmentIndex]?.imageUrlUploads?.[0]?.voiceClip?.voiceId || "").trim() ||
      (baseForm.multiSpeakerDialogue ? null : baseForm.characterVoiceId) ||
      null,
    prompt: segmentContinuityPrompt(
      sharedBrief,
      segmentIndex,
      totalSegments,
      // Keep mention tags in the script so "@Image: Hello" still parses as dialogue.
      sceneParsed.hasDialogue ? scenePromptRaw : segmentScript,
      { multiSpeaker: Boolean(baseForm.multiSpeakerDialogue) },
    ),
  };

  const multiSpeaker = Boolean(baseForm.multiSpeakerDialogue);
  const anchorImages = multiSpeaker ? [] : getAnchorCharacterUploads(baseForm);

  if (!multiSpeaker && segmentIndex === 0 && anchorImages.length) {
    // Lock identity on the opening clip by mirroring uploads into reference images.
    form.referenceImageUploads = anchorImages;
  }

  // Per-scene media wins over chain-frame continuity when provided.
  if (sceneImages.length) {
    const grok15 = String(baseForm.model || "").toLowerCase().includes("grok-imagine-video");
    // PoYo Grok Imagine Video accepts a single start frame; for other models
    // allow multiple scene images by splitting into start frames + references.
    // Multi-speaker dialogue: always one image per turn (never mix characters).
    const startFrames = multiSpeaker
      ? sceneImages.slice(0, 1)
      : sceneImages.slice(0, grok15 ? 1 : 2);
    const extraRefs = multiSpeaker ? [] : sceneImages.slice(grok15 ? 1 : 2, 5);
    form.imageUrlUploads = startFrames;
    const mergedRefs = [...anchorImages, ...extraRefs];
    if (mergedRefs.length) {
      form.referenceImageUploads = mergedRefs;
    } else if (multiSpeaker) {
      form.referenceImageUploads = [];
    }
  } else if (!multiSpeaker && chainFrameUrl) {
    const chainItem = {
      id: `chain-frame-${segmentIndex}`,
      previewUrl: chainFrameUrl,
      name: `segment-${segmentIndex + 1}-continuity.png`,
    };
    // Chain frame = motion/pose continuity; anchor images = face/gender/identity lock.
    form.imageUrlUploads = [chainItem];
    if (anchorImages.length) {
      form.referenceImageUploads = anchorImages;
    }
  }

  if (characterAudios.length) {
    form.voiceSource = "upload";
    form.referenceAudioUploads = characterAudios.slice(0, 5);
  }

  // Multi-speaker: never unify/chain voices across turns.
  if (multiSpeaker) {
    return form;
  }

  return applyStitchVoiceSettings(form, { segmentIndex, voiceLock, chainAudioUrl });
}
